import React, { useState } from 'react';
import { X, CheckCircle2, MessageSquare } from 'lucide-react';
import { Task } from '../../types';

interface TaskDetailsProps {
  task: Task;
  categories: Array<{ id: string; name: string; color: string }>;
  onClose: () => void;
  onUpdate: (taskId: string, updates: Partial<Task>) => void;
}

export default function TaskDetails({ task, categories, onClose, onUpdate }: TaskDetailsProps) {
  const [comment, setComment] = useState('');

  const handleProgressUpdate = (progress: number) => {
    onUpdate(task.id, { progress });
  };

  const handleAddComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;

    onUpdate(task.id, {
      comments: [...(task.comments || []), {
        id: crypto.randomUUID(),
        text: comment,
        user: 'Current User', // In a real app, get from auth context
        timestamp: new Date().toISOString()
      }]
    });
    setComment('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">{task.title}</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Details</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={task.status}
                    onChange={(e) => onUpdate(task.id, { status: e.target.value as Task['status'] })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Progress</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={task.progress || 0}
                    onChange={(e) => handleProgressUpdate(Number(e.target.value))}
                    className="mt-1 block w-full"
                  />
                  <div className="mt-1 text-sm text-gray-600 text-right">
                    {task.progress || 0}%
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Due Date</label>
                  <input
                    type="date"
                    value={task.dueDate.split('T')[0]}
                    onChange={(e) => onUpdate(task.id, { dueDate: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Assignee</label>
                  <input
                    type="text"
                    value={task.assignee || ''}
                    onChange={(e) => onUpdate(task.id, { assignee: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Comments</h4>
              <div className="space-y-4">
                <div className="h-48 overflow-y-auto space-y-4">
                  {task.comments?.map((comment) => (
                    <div key={comment.id} className="flex space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-800">
                            {comment.user[0].toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm">
                          <span className="font-medium text-gray-900">{comment.user}</span>
                        </div>
                        <div className="mt-1 text-sm text-gray-700">
                          {comment.text}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {new Date(comment.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <form onSubmit={handleAddComment} className="mt-4">
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                    <button
                      type="submit"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Comment
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}